import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { User as OidcUser } from "oidc-client-ts";
import { resolveRole, type AppRole, type DecodedIdToken } from "./oidcConfig";
import { userManager } from "./userManager";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

/**
 * Revokes the token + terminates the Asgardeo server-side session via the
 * backend (see cloudflare_doh_backend's POST /api/auth/logout) before the
 * client-side signout redirect. Neither the web nor mobile app can revoke
 * tokens correctly on its own — that call must be authenticated as the
 * confidential client, which only the backend has credentials for. This
 * must never throw: a failed backend call should still fall through to
 * userManager.signoutRedirect() so the user isn't stuck logged in locally.
 */
async function revokeSessionOnBackend(accessToken: string, refreshToken: string | undefined): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      // client: "web" tells the backend which Asgardeo app's credentials
      // own this token — web and mobile are separate Asgardeo apps on the
      // same tenant, not one app shared by both.
      body: JSON.stringify({ refreshToken, client: "web" }),
    });
  } catch (err) {
    console.warn("Backend logout call failed, proceeding with client-side signout:", err);
  }
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  role: AppRole;
  profile: DecodedIdToken | null;
  medicalEstablishmentId: string | undefined;
  login: (options?: { signup?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Access token is held only in the UserManager's in-memory/sessionStorage
 * state (oidc-client-ts default: sessionStorage, not localStorage), never
 * written to a cookie or a variable that outlives the tab. This is more
 * XSS-exposed than an httpOnly session cookie would be — the original
 * NextAuth setup had that property — but NextAuth's actual behavior was
 * to attach the token to almost nothing (1 of 55 calls), so this is a net
 * increase in real protection despite the different threat model. A BFF
 * proxy through the Worker would close this gap entirely; not built here
 * because it costs a Worker invocation per API call for no benefit at
 * current scale (5 testers) — see the plan's auth section.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<OidcUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    userManager
      .getUser()
      .then((u) => setUser(u && !u.expired ? u : null))
      .finally(() => setIsLoading(false));

    const onLoaded = (u: OidcUser) => setUser(u);
    const onUnloaded = () => setUser(null);
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    return () => {
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
    };
  }, []);

  const profile = (user?.profile as DecodedIdToken | undefined) ?? null;
  const role = resolveRole(profile?.roles);

  const logout = async () => {
    if (user?.access_token) {
      await revokeSessionOnBackend(user.access_token, user.refresh_token);
    }

    // The stored id_token can outlive its own `exp` (automaticSilentRenew
    // only guarantees a fresh access_token — a renew response that omits
    // id_token leaves the original in place). Asgardeo rejects an expired
    // id_token_hint and 302s to its error page instead of completing
    // end_session, which is what "Something went wrong" was. Dropping the
    // stored user first makes _signoutStart send no id_token_hint at all,
    // and Asgardeo's createSignoutRequest then falls back to client_id,
    // which it accepts.
    const idTokenExpired = typeof user?.profile.exp === "number" && user.profile.exp * 1000 < Date.now();
    if (idTokenExpired) {
      await userManager.removeUser();
    }

    await userManager.clearStaleState();
    await userManager.signoutRedirect();
  };

  const value: AuthContextValue = {
    isAuthenticated: Boolean(user && !user.expired),
    isLoading,
    accessToken: user?.access_token ?? null,
    role,
    profile,
    medicalEstablishmentId: profile?.sub,
    // prompt: "login" forces the credentials screen even when Asgardeo's
    // own SSO cookie is still alive (e.g. logout's end_session redirect
    // didn't fully clear it) — without this, "Login"/"Sign up" can
    // silently re-authenticate the previous session with no prompt.
    // signup mirrors doh_mobile_frontend's authenticate(isSignup) —
    // extraQueryParams: { signup: "true" } routes to Asgardeo's
    // self-registration flow instead of straight login.
    login: (options) =>
      userManager.signinRedirect({
        prompt: "login",
        ...(options?.signup ? { extraQueryParams: { signup: "true" } } : {}),
      }),
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
